import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useOrgStructureSyncStatus, useSyncOrgStructure } from "@/hooks/useConfigurationServer";
import { errorDetailOr } from "@/lib/api-error";
import { useManagementDraft } from "./managementDraftContext";

/**
 * A sincronização e o que a trava: serve ao botão do cabeçalho, ao alerta e à Extração.
 * Fora do Gerenciamento não há rascunho, então `hasDraft` é sempre falso.
 */
export function useOrgStructureSyncRun(projectId: string) {
  const { t } = useTranslation();
  const status = useOrgStructureSyncStatus(projectId);
  const sync = useSyncOrgStructure(projectId);
  const draft = useManagementDraft();
  // Operações que se anulam deixam `changes` em 0, mas o rascunho ainda existe.
  const hasDraft = (draft?.pendingOperations ?? 0) > 0;
  const needsSync = status.data?.needs_sync === true;

  const run = async () => {
    try {
      const result = await sync.mutateAsync();
      const message = t("Structure synced: {{approved}} approved, {{out}} out of scope, {{pending}} pending.",
        { approved: result.approved, out: result.out_of_scope, pending: result.pending });
      const clientValidation = result.out_of_scope_client_validation;
      const hidden = result.out_of_scope_hidden;
      // O detalhe do mesmo resultado: por que parte do "fora do escopo" foi para lá (D13).
      if (clientValidation + hidden > 0) {
        toast.success(message, {
          description: t("Out of scope: {{clientValidation}} marked \"Validate with client\" and {{hidden}} not shown in the Diagnostic.",
            { clientValidation, hidden }),
        });
      } else {
        toast.success(message);
      }
      if (result.preserved > 0) {
        toast.warning(t("{{count}} units in execution or already in the CBC kept their decision.", { count: result.preserved }));
      }
    } catch (error) {
      toast.error(errorDetailOr(error, t("Could not sync with the Diagnostic.")));
    }
  };

  return { status, needsSync, hasDraft, pending: sync.isPending, run };
}
